import React, { useState, useEffect, useRef } from 'react';

const API_BASE_URL = 'https://ufilm-upload-api.ott-f87.workers.dev';

const VideoManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [editingVideo, setEditingVideo] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const thumbnailInputRef = useRef(null);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ufilm_token');
      const response = await fetch(`${API_BASE_URL}/api/videos/my-videos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setVideos(data.videos);
    } catch (error) {
      console.error('Failed to load videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('ufilm_token');
      const response = await fetch(`${API_BASE_URL}/api/videos/delete/${videoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) { alert('Video deleted successfully!'); loadVideos(); }
      else alert('Failed to delete video: ' + data.error);
    } catch (error) { alert('Failed to delete video'); }
  };

  // ─── THUMBNAIL UPLOAD ───────────────────────────────────────────────────────
  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file (JPG, PNG, WebP)'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setThumbnailPreview(ev.target.result);
    reader.readAsDataURL(file);
    setEditingVideo(prev => ({ ...prev, _thumbnailFile: file }));
  };

  const uploadThumbnailToCloudflare = async (videoId, file) => {
    setThumbnailUploading(true);
    try {
      const token = localStorage.getItem('ufilm_token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/videos/thumbnail/${videoId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (data.success) return data.thumbnailUrl || true;
      else { alert('Thumbnail upload failed: ' + (data.error || 'Unknown error')); return false; }
    } catch (error) {
      alert('Thumbnail upload failed');
      return false;
    } finally {
      setThumbnailUploading(false);
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('ufilm_token');

      // Upload thumbnail first if selected
      if (editingVideo._thumbnailFile) {
        const thumbResult = await uploadThumbnailToCloudflare(editingVideo.id, editingVideo._thumbnailFile);
        if (!thumbResult) return;
      }

      const response = await fetch(`${API_BASE_URL}/api/videos/update/${editingVideo.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingVideo.title,
          description: editingVideo.description,
          category: editingVideo.category,
          tags: editingVideo.tags,
          contentRating: editingVideo.contentRating || 'U',
          posterUrl: editingVideo.posterUrl || '',
          heroBannerUrl: editingVideo.heroBannerUrl || '',
          cast: editingVideo.cast || '',
          director: editingVideo.director || '',
          synopsis: editingVideo.synopsis || '',
          isFree: editingVideo.isFree !== undefined ? editingVideo.isFree : true,
          contentType: editingVideo.contentType || 'movie',
          seriesName: editingVideo.seriesName || '',
          episodeNumber: editingVideo.episodeNumber || ''
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Video updated successfully!');
        setEditingVideo(null);
        setThumbnailPreview(null);
        loadVideos();
      } else alert('Failed to update video: ' + data.error);
    } catch (error) { alert('Failed to update video'); }
  };

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         video.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || video.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#718096' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <h3>Loading videos...</h3>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '36px', color: '#2d3748', marginBottom: '10px' }}>📹 Video Management</h1>
        <p style={{ color: '#718096', fontSize: '16px' }}>Manage your uploaded videos • Total: {videos.length}</p>
      </div>

      {/* Filters */}
      <div style={{
        background: '#f7fafc', padding: '24px', borderRadius: '16px', marginBottom: '30px',
        display: 'grid', gridTemplateColumns: '1fr 300px', gap: '16px'
      }}>
        <input type="text" placeholder="🔍 Search videos..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: '12px 20px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '15px' }} />
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
          style={{ padding: '12px 20px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '15px', cursor: 'pointer' }}>
          <option value="">All Categories</option>
          <option value="family">Family</option>
          <option value="romance">Romance</option>
          <option value="action">Action Thriller</option>
          <option value="horror">Horror</option>
          <option value="mythology">Mythology</option>
          <option value="patriotic">Patriotic</option>
          <option value="music">Music</option>
          <option value="documentary">Documentary</option>
          <option value="theater">Theater</option>
          <option value="suspense">Suspense Crime</option>
        </select>
      </div>

      {/* Video Grid */}
      {filteredVideos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', background: '#f7fafc', borderRadius: '16px', color: '#718096' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎬</div>
          <h3 style={{ fontSize: '24px', marginBottom: '10px' }}>No videos found</h3>
          <p>Upload your first video to get started!</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
          {filteredVideos.map(video => (
            <div key={video.id} style={{
              background: 'white', border: '2px solid #e2e8f0', borderRadius: '16px',
              overflow: 'hidden', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer'
            }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ position: 'relative', paddingTop: '177.78%', background: '#000', overflow: 'hidden' }}>
                {video.thumbnailUrl ? (
                  <img src={video.thumbnailUrl} alt={video.title} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '64px', color: '#fff' }}>🎬</div>
                )}
                <div style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(0,0,0,0.8)', color: 'white', padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                  {formatDuration(video.duration)}
                </div>
                {video.contentRating && (
                  <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#E8B84B', color: 'black', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' }}>
                    {video.contentRating}
                  </div>
                )}
              </div>

              <div style={{ padding: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2d3748', marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {video.title}
                </h3>
                <p style={{ fontSize: '14px', color: '#718096', marginBottom: '12px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {video.description || 'No description'}
                </p>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {video.category && (
                    <span style={{ background: '#edf2f7', color: '#4a5568', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                      {video.category}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingTop: '12px', borderTop: '1px solid #e2e8f0', marginBottom: '16px' }}>
                  <span style={{ fontSize: '13px', color: '#a0aec0' }}>📅 {formatDate(video.createdAt)}</span>
                  <span style={{ fontSize: '13px', color: '#a0aec0' }}>👁️ {video.views} views</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setPreviewVideo(video)} style={{ flex: 1, padding: '10px', background: '#4299e1', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>▶️ Preview</button>
                  <button onClick={() => { setEditingVideo(video); setThumbnailPreview(null); }} style={{ flex: 1, padding: '10px', background: '#48bb78', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>✏️ Edit</button>
                  <button onClick={() => handleDelete(video.id)} style={{ padding: '10px', background: '#f56565', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewVideo && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '20px' }}
          onClick={() => setPreviewVideo(null)}>
          <div style={{ background: '#000', borderRadius: '16px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px', background: '#1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ color: 'white', margin: 0 }}>{previewVideo.title}</h3>
              <button onClick={() => setPreviewVideo(null)} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', padding: '0 10px' }}>×</button>
            </div>
            <div style={{ position: 'relative', paddingTop: '177.78%' }}>
              <iframe title={previewVideo.title}
                src={`https://customer-ufktx4adx5q66gvm.cloudflarestream.com/${previewVideo.id}/iframe?primaryColor=%23667eea`}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowFullScreen />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingVideo && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '40px', maxWidth: '600px', width: '100%', margin: 'auto' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>✏️ Edit Video</h2>

            {/* ─── THUMBNAIL UPLOAD SECTION ─── */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: '700', marginBottom: '12px', fontSize: '15px', color: '#2d3748' }}>
                🖼️ Poster / Thumbnail
              </label>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                {/* Current or Preview Thumbnail */}
                <div style={{ width: '100px', flexShrink: 0 }}>
                  <div style={{ width: '100px', height: '178px', borderRadius: '8px', overflow: 'hidden', background: '#f0f0f0', border: '2px solid #e2e8f0' }}>
                    <img
                      src={thumbnailPreview || editingVideo.thumbnailUrl}
                      alt="Thumbnail"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  {thumbnailPreview && (
                    <div style={{ textAlign: 'center', marginTop: '4px', fontSize: '11px', color: '#48bb78', fontWeight: '600' }}>
                      ✅ New poster
                    </div>
                  )}
                </div>

                {/* Upload controls */}
                <div style={{ flex: 1 }}>
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleThumbnailSelect}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => thumbnailInputRef.current.click()}
                    style={{
                      width: '100%', padding: '12px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px',
                      fontWeight: '600', cursor: 'pointer', marginBottom: '8px'
                    }}
                  >
                    📁 Choose Poster Image
                  </button>
                  <div style={{ fontSize: '12px', color: '#718096', lineHeight: '1.5' }}>
                    ✅ Recommended: <strong>1080 × 1920px</strong> (9:16)<br />
                    ✅ Format: JPG or PNG<br />
                    ✅ Max size: 5MB
                  </div>
                  {thumbnailPreview && (
                    <button
                      onClick={() => { setThumbnailPreview(null); setEditingVideo(prev => ({ ...prev, _thumbnailFile: null })); }}
                      style={{ marginTop: '8px', padding: '6px 12px', background: '#fed7d7', color: '#c53030', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      ✕ Remove new poster
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* ─────────────────────────────── */}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>Title</label>
              <input type="text" value={editingVideo.title}
                onChange={(e) => setEditingVideo({...editingVideo, title: e.target.value})}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>Description</label>
              <textarea value={editingVideo.description}
                onChange={(e) => setEditingVideo({...editingVideo, description: e.target.value})}
                rows={3}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>🖼️ Poster URL (Firebase)</label>
              <input type="text" value={editingVideo.posterUrl || ''}
                onChange={(e) => setEditingVideo({...editingVideo, posterUrl: e.target.value})}
                placeholder="https://firebasestorage.googleapis.com/..."
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>🎬 Hero Banner URL (Firebase)</label>
              <input type="text" value={editingVideo.heroBannerUrl || ''}
                onChange={(e) => setEditingVideo({...editingVideo, heroBannerUrl: e.target.value})}
                placeholder="https://firebasestorage.googleapis.com/..."
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>📽️ Content Type</label>
              <select value={editingVideo.contentType || 'movie'} onChange={(e) => setEditingVideo({...editingVideo, contentType: e.target.value})}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box' }}>
                <option value="movie">🎬 Movie</option>
                <option value="webseries">📺 Web Series</option>
                <option value="shortfilm">🎥 Short Film</option>
                <option value="music">🎵 Music Video</option>
              </select>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>📺 Series Name (if Web Series)</label>
              <input type="text" value={editingVideo.seriesName || ''}
                onChange={(e) => setEditingVideo({...editingVideo, seriesName: e.target.value})}
                placeholder="e.g. Missed Call"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>🔢 Episode Number</label>
              <input type="number" value={editingVideo.episodeNumber || ''}
                onChange={(e) => setEditingVideo({...editingVideo, episodeNumber: e.target.value})}
                placeholder="e.g. 1"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '20px', display:'flex', alignItems:'center', justifyContent:'space-between',
              background: editingVideo.isFree !== false ? 'rgba(72,187,120,0.1)' : 'rgba(232,184,75,0.1)',
              border: editingVideo.isFree !== false ? '2px solid #48bb78' : '2px solid #E8B84B',
              borderRadius:'12px', padding:'16px' }}>
              <div>
                <div style={{ fontWeight:'700', fontSize:'15px', color: editingVideo.isFree !== false ? '#48bb78' : '#E8B84B' }}>
                  {editingVideo.isFree !== false ? '🆓 FREE' : '🔒 PREMIUM'}
                </div>
                <div style={{ fontSize:'12px', color:'#888', marginTop:'4px' }}>
                  {editingVideo.isFree !== false ? 'Visible to all users' : 'Subscription required'}
                </div>
              </div>
              <label style={{ position:'relative', display:'inline-block', width:'52px', height:'28px', cursor:'pointer' }}>
                <input type="checkbox" checked={editingVideo.isFree !== false}
                  onChange={e => setEditingVideo({...editingVideo, isFree: e.target.checked})}
                  style={{ opacity:0, width:0, height:0 }}/>
                <span style={{
                  position:'absolute', inset:0, borderRadius:'34px', transition:'.3s',
                  background: editingVideo.isFree !== false ? '#48bb78' : '#E8B84B'
                }}>
                  <span style={{
                    position:'absolute', height:'20px', width:'20px', left: editingVideo.isFree !== false ? '28px' : '4px',
                    bottom:'4px', background:'white', borderRadius:'50%', transition:'.3s'
                  }}/>
                </span>
              </label>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>🎭 Cast (comma separated)</label>
              <input type="text" value={editingVideo.cast || ''}
                onChange={(e) => setEditingVideo({...editingVideo, cast: e.target.value})}
                placeholder="Actor 1, Actor 2, Actor 3"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>🎬 Director</label>
              <input type="text" value={editingVideo.director || ''}
                onChange={(e) => setEditingVideo({...editingVideo, director: e.target.value})}
                placeholder="Director name"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>📖 Synopsis (max 5 lines)</label>
              <textarea value={editingVideo.synopsis || ''}
                onChange={(e) => setEditingVideo({...editingVideo, synopsis: e.target.value})}
                rows={4} placeholder="Story synopsis..."
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>Category</label>
              <select value={editingVideo.category} onChange={(e) => setEditingVideo({...editingVideo, category: e.target.value})}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px' }}>
                <option value="">Select category</option>
                <option value="family">Family</option>
                <option value="romance">Romance</option>
                <option value="action">Action Thriller</option>
                <option value="horror">Horror</option>
                <option value="mythology">Mythology</option>
                <option value="patriotic">Patriotic</option>
                <option value="music">Music</option>
                <option value="documentary">Documentary</option>
                <option value="theater">Theater</option>
                <option value="suspense">Suspense Crime</option>
                <option value="scifi">Sci-Fi & Fantasy</option>
                <option value="comedy">Comedy</option>
                <option value="genz">Gen Z - Zoomers</option>
                <option value="genalpha">Gen Alpha</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>Content Rating</label>
              <select value={editingVideo.contentRating || 'U'} onChange={(e) => setEditingVideo({...editingVideo, contentRating: e.target.value})}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px' }}>
                <option value="U">U - Universal (All Ages)</option>
                <option value="U/A 7+">U/A 7+ - Parental Guidance</option>
                <option value="U/A 12+">U/A 12+ - Parental Guidance</option>
                <option value="U/A 13+">U/A 13+ - Parental Guidance</option>
                <option value="U/A 16+">U/A 16+ - Parental Guidance</option>
                <option value="A">A - Adults Only (18+)</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
              <button onClick={() => { setEditingVideo(null); setThumbnailPreview(null); }}
                style={{ flex: 1, padding: '14px', background: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleUpdate} disabled={thumbnailUploading}
                style={{ flex: 1, padding: '14px', background: thumbnailUploading ? '#a0aec0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: thumbnailUploading ? 'not-allowed' : 'pointer' }}>
                {thumbnailUploading ? '⏳ Uploading...' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoManagement;
